import { createContext, useContext } from 'react';
import type { TenantConfig } from '@pink/shared';

import type { ConfigFetchErrorKind } from './fetch-tenant-config.js';

/**
 * Bootstrap-Status der App während/nach dem Tenant-Config-Fetch.
 *
 * Diskriminierte Union, damit `<App>` ohne Casts auf den jeweils erlaubten
 * Inhalt zugreifen kann (Skeleton während Fetch, Children mit Config,
 * Technical-Error-Screen bei Bootstrap-Fehler).
 */
export type BootstrapState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly config: TenantConfig }
  | {
      readonly status: 'error';
      readonly errorKind: ConfigFetchErrorKind;
      readonly message: string;
    };

/**
 * Context-Wert: aktueller Bootstrap-Status + die geladene Config (nur im
 * `ready`-Branch verfügbar — der Hook prüft das und wirft, wenn ein
 * Konsument außerhalb des Ready-Zweigs versucht, die Config zu lesen).
 */
export interface TenantConfigContextValue {
  readonly bootstrap: BootstrapState;
}

export const TenantConfigContext = createContext<TenantConfigContextValue | undefined>(undefined);

/**
 * Hook für lesenden Zugriff auf den Bootstrap-Status. Komponenten unterhalb
 * von `<ConfigProvider>` rufen das auf; ohne Provider wirft der Hook
 * absichtlich, damit Fehler früh sichtbar werden.
 */
export function useBootstrap(): BootstrapState {
  const ctx = useContext(TenantConfigContext);
  if (ctx === undefined) {
    throw new Error('useBootstrap must be used within <ConfigProvider>.');
  }
  return ctx.bootstrap;
}

/**
 * Hook für lesenden Zugriff auf die fertig geladene Tenant-Config. Wirft, wenn
 * der Bootstrap noch nicht im `ready`-State ist — Komponenten, die das nicht
 * garantieren können, sollen stattdessen `useBootstrap` nutzen und auf den
 * Status reagieren.
 */
export function useTenantConfig(): TenantConfig {
  const bootstrap = useBootstrap();
  if (bootstrap.status !== 'ready') {
    throw new Error('useTenantConfig called before bootstrap completed.');
  }
  return bootstrap.config;
}
